import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiExternalLink, FiArrowRight } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  background-color: #ffffff;
  border-radius: var(--border-radius-md);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
    max-height: 80vh;
  }
  
  .dark-mode & {
    background: #0e1420;
  }
`;

const ModalImageContainer = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  
  @media (min-width: 992px) {
    height: 100%;
  }
`;

const ModalImageWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled(motion.img)`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
`;



const ModalBody = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ModalCategory = styled.span`
  color: var(--white);
  font-size: 0.9rem;
  background-color: ${props => props.bgColor || 'var(--primary)'};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 1rem;
  width: fit-content;
`;

const ModalTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--primary);
  
  .dark-mode & {
    color: var(--white);
  }
`;

const ModalDescription = styled.p`
  color: var(--gray-600);
  line-height: 1.8;
  margin-bottom: 2rem;
  
  .dark-mode & {
    color: var(--light);
    opacity: 0.8;
  }
`;

const ModalFeatures = styled.ul`
  list-style-type: none;
  margin-bottom: 1.5rem;
`;

const ModalFeature = styled.li`
  margin-bottom: 0.8rem;
  display: flex;
  align-items: flex-start;
  color: var(--dark);
  
  .dark-mode & {
    color: var(--light);
  }
  
  &::before {
    content: '';
    flex-shrink: 0;
    display: block;
    width: 8px;
    height: 8px;
    margin-top: 8px;
    margin-right: 10px;
    border-radius: 50%;
    background-color: ${props => props.color || 'var(--primary)'};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  align-items: center;
  justify-content: flex-start;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
`;

const ModalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--secondary);
  color: var(--dark);
  font-weight: 600;
  padding: 0.7rem 1.3rem;
  border-radius: var(--border-radius-sm);
  text-decoration: none;
  font-size: 1rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s;
  
  &:hover {
    background: var(--primary);
    color: var(--white);
    transform: translateY(-2px) scale(1.04);
    box-shadow: var(--shadow-md);
  }
  
  svg {
    font-size: 1.1em;
  }
`;

const NoLinkText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-500);
  font-weight: 500;
  font-style: italic;
  font-size: 0.9rem;
  
  .dark-mode & {
    color: var(--gray-400);
  }
`;

const ModalButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--primary);
  color: var(--white);
  padding: 0.8rem 1.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--secondary);
    color: var(--black);
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }
  
  svg {
    transition: transform var(--transition-fast);
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--secondary);
    color: var(--black);
    transform: rotate(90deg);
  }
`;

/* Belgian accent removed: no vertical flag in modal */

const ProjectModal = ({ project, isOpen, onClose, categoryColors }) => {
  // Si project est null ou si le modal n'est pas ouvert, ne rien afficher
  if (!project || !isOpen) {
    return null;
  }
  
  const getCategoryColor = () => {
    switch(project.category) {
      case 'site-vitrine':
        return categoryColors?.siteVitrine || 'var(--black)';
      case 'e-commerce':
        return categoryColors?.ecommerce || 'var(--secondary)';
      case 'application':
        return categoryColors?.application || 'var(--accent)';
      default:
        return 'var(--primary)';
    }
  };
  
  const getCategoryName = () => {
    switch(project.category) {
      case 'site-vitrine':
        return 'Site Vitrine';
      case 'e-commerce':
        return 'E-commerce';
      case 'application':
        return 'Application Web';
      default:
        return project.category;
    }
  };
  
  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      transition: { 
        duration: 0.4,
        type: "spring",
        stiffness: 300,
        damping: 25
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      transition: { 
        duration: 0.3 
      } 
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <ModalContent
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseButton onClick={onClose}>
              <FiX />
            </ModalCloseButton>
            
            <ModalImageContainer>
              <ModalImageWrapper>
                <ModalImage 
                  src={project.image} 
                  alt={project.title} 
                />
              </ModalImageWrapper>
            </ModalImageContainer>
            
            <ModalBody>
              <ModalCategory bgColor={getCategoryColor()}>
                {getCategoryName()}
              </ModalCategory>
              <ModalTitle>{project.title}</ModalTitle>
              <ModalDescription>{project.description}</ModalDescription>
              
              <ModalFeatures>
                {project.features.map((feature, index) => (
                  <ModalFeature key={index} color={getCategoryColor()}>
                    {feature}
                  </ModalFeature>
                ))}
              </ModalFeatures>
              
              <ModalFooter>
                {project.link ? (
                  <ModalLink href={project.link} target="_blank" rel="noopener noreferrer">
                    Voir le site <FiExternalLink />
                  </ModalLink>
                ) : (
                  <NoLinkText>
                    Projet non publié en ligne
                  </NoLinkText>
                )}
                <ModalButton href="#contact" onClick={onClose}>
                  Discuter d'un projet similaire <FiArrowRight />
                </ModalButton>
              </ModalFooter>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
